import { useState, useRef, useCallback, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Upload, FileUp, X, AlertTriangle, Wrench, FileWarning, Clock, Trash2, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useOrgQuota } from "@/hooks/useOrgQuota";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { DuplicateDocumentModal } from "@/components/DuplicateDocumentModal";
import { LoadingToast } from "@/components/LoadingToast";
import { AssignEmbarqueModal } from "@/components/AssignEmbarqueModal";
import { ManualIngestModal } from "@/components/ManualIngestModal";
import { useFailedJobs } from "@/hooks/useFailedJobs";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import type { RealtimeChannel } from "@supabase/supabase-js";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PendingData {
  file: File;
  archivoUrl: string;
  noDocumento: string;
  existingCount: number;
  existingIds?: string[];
  extractedPayload?: any;
}

type ToastStatus = "loading" | "success" | "error";

export function ProcessDocumentModal({ open, onOpenChange }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [pendingData, setPendingData] = useState<PendingData | null>(null);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [assignDocId, setAssignDocId] = useState<string | null>(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  // Kill Switch state
  const [killSwitchJobId, setKillSwitchJobId] = useState<string | null>(null);
  const [killSwitchArchivoUrl, setKillSwitchArchivoUrl] = useState<string | null>(null);
  const [killSwitchOpen, setKillSwitchOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast, dismiss } = useToast();
  const { user, orgId } = useAuth();
  const isMobile = useIsMobile();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const quota = useOrgQuota();
  const failedJobs = useFailedJobs();
  const queryClient = useQueryClient();

  // Keep dismiss in a ref so the WebSocket callback always uses the latest version
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;

  const toastStatusRef = useRef<{
    setStatus: (s: ToastStatus) => void;
    setLabel: (l: string) => void;
    setKillSwitchContext: (jobId: string, archivoUrl: string) => void;
  } | null>(null);
  const toastIdRef = useRef<string | null>(null);

  const cleanupChannel = useCallback(() => {
    if (channelRef.current) {
      console.log("🔵 Limpiando canal Realtime");
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  // Cleanup channel ONLY on true unmount
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        console.log("🔵 Componente desmontado — limpiando canal Realtime");
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, []);

  const launchKillSwitch = useCallback((jobId: string, archivoUrl: string) => {
    setKillSwitchJobId(jobId);
    setKillSwitchArchivoUrl(archivoUrl);
    setKillSwitchOpen(true);
  }, []);

  // Descartar un job fallido sin consumir crédito (no se digita ni registra documento)
  const [discardJobId, setDiscardJobId] = useState<string | null>(null);
  const [discardFileName, setDiscardFileName] = useState<string>("");
  const [discarding, setDiscarding] = useState(false);

  const requestDiscard = useCallback((jobId: string, fileName: string) => {
    setDiscardJobId(jobId);
    setDiscardFileName(fileName);
  }, []);

  const confirmDiscard = useCallback(async () => {
    if (!discardJobId) return;
    setDiscarding(true);
    try {
      const { error } = await supabase
        .from("document_jobs" as any)
        .update({
          job_metadata: {
            recovered_via: "discarded",
            discarded_at: new Date().toISOString(),
            discarded_by: user?.id ?? null,
          },
        } as any)
        .eq("id", discardJobId);
      if (error) throw error;
      toast({ title: "Procesamiento descartado", description: "No se consumió crédito." });
      queryClient.invalidateQueries({ queryKey: ["failed-jobs"] });
      setDiscardJobId(null);
    } catch (err: any) {
      toast({ title: "No se pudo descartar", description: err.message ?? "Error desconocido", variant: "destructive" });
    } finally {
      setDiscarding(false);
    }
  }, [discardJobId, user?.id, toast, queryClient]);

  const showLoadingToast = useCallback(
    (fileName: string) => {
      let currentStatus: ToastStatus = "loading";
      let currentLabel = "Procesando documento";
      let currentJobId: string | null = null;
      let currentArchivoUrl: string | null = null;

      const ToastWrapper = () => {
        const [status, setStatus] = useState<ToastStatus>(currentStatus);
        const [label, setLabel] = useState(currentLabel);
        const [jobId, setJobId] = useState<string | null>(currentJobId);
        const [archivoUrl, setArchivoUrl] = useState<string | null>(currentArchivoUrl);

        toastStatusRef.current = {
          setStatus: (s) => { currentStatus = s; setStatus(s); },
          setLabel: (l) => { currentLabel = l; setLabel(l); },
          setKillSwitchContext: (jId, aUrl) => {
            currentJobId = jId; currentArchivoUrl = aUrl;
            setJobId(jId); setArchivoUrl(aUrl);
          },
        };

        const onKillSwitch = status === "error" && jobId && archivoUrl
          ? () => {
              dismissRef.current(toastIdRef.current!);
              launchKillSwitch(jobId, archivoUrl);
            }
          : undefined;

        return <LoadingToast status={status} label={label} fileName={fileName} onKillSwitch={onKillSwitch} />;
      };

      const { id, dismiss: dismissThis } = toast({
        duration: Infinity,
        description: <ToastWrapper />,
        className: "p-4",
      });
      toastIdRef.current = id;

      return { id, dismiss: dismissThis };
    },
    [toast, launchKillSwitch],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const uploadToStorage = async (fileToUpload: File): Promise<string> => {
    if (!user?.id) throw new Error("Usuario no autenticado");
    const safeName = `${user.id}_${fileToUpload.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
    const { error } = await supabase.storage.from("documentos_kargo").upload(safeName, fileToUpload, { upsert: true });
    if (error) throw new Error(`Error en Storage: ${error.message}`);
    return safeName;
  };

  const createJob = async (archivoUrl: string): Promise<string> => {
    if (!user?.id || !orgId) throw new Error("Faltan credenciales");
    const { data, error } = await supabase
      .from("document_jobs" as any)
      .insert({ user_id: user.id, org_id: orgId, archivo_url: archivoUrl, status: "pending" } as any)
      .select("id")
      .single();
    if (error) throw new Error(`Error creando job: ${error.message}`);
    return (data as any).id;
  };

  const subscribeToJob = (jobId: string, toastId: string) => {
    cleanupChannel();

    console.log("🔵 Suscribiendo a canal Realtime para job:", jobId);

    const channel = supabase
      .channel(`job-updates-${jobId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "document_jobs",
          filter: `id=eq.${jobId}`,
        },
        (payload: any) => {
          console.log("🟢 WEBSOCKET PAYLOAD RECIBIDO:", JSON.stringify(payload.new, null, 2));
          const newStatus = payload.new?.status;
          const documentId = payload.new?.document_id;
          const errorMsg = payload.new?.error_message;

          if (newStatus === "processing") {
            toastStatusRef.current?.setLabel("Extrayendo con IA...");
          } else if (newStatus === "completed") {
            toastStatusRef.current?.setStatus("success");
            toastStatusRef.current?.setLabel("Documento analizado con éxito");
            setTimeout(() => dismissRef.current(toastId), 3000);
            cleanupChannel();

            if (documentId) {
              console.log("🟢 EJECUTANDO APERTURA DE MODAL. DocumentID:", documentId);
              setAssignDocId(documentId);
              setAssignModalOpen(true);
            } else {
              console.error("🔴 ERROR: El WebSocket reportó 'completed' pero documentId vino vacío/null.");
            }
          } else if (newStatus === "error") {
            // Solo tratar como conflicto si: (a) viene el prefijo Y (b) el JSON tiene firma de conflicto real
            // Esto previene que errores de validación (400) disfrazados como 409_CONFLICT por n8n
            // disparen el modal de duplicado y atrapen al usuario en un bucle.
            let isRealConflict = false;
            let conflictData: any = null;

            if (errorMsg && errorMsg.startsWith("409_CONFLICT::")) {
              const jsonStr = errorMsg.replace("409_CONFLICT::", "");
              try {
                conflictData = JSON.parse(jsonStr);
                // Firma de conflicto real: existing_ids con al menos 1 elemento
                isRealConflict = Array.isArray(conflictData?.existing_ids) &&
                  conflictData.existing_ids.length > 0;
              } catch {
                isRealConflict = false;
              }
            }

            if (isRealConflict && conflictData) {
              setPendingData({
                file: new File([], conflictData.no_documento || "unknown"),
                archivoUrl: payload.new?.archivo_url || "",
                noDocumento: conflictData.no_documento || "",
                existingCount: conflictData.existing_count || 1,
                existingIds: conflictData.existing_ids || [],
                extractedPayload: conflictData.extracted_payload || null,
              });
              dismissRef.current(toastId);
              setDuplicateModalOpen(true);
            } else {
              // Error real (validación, red, RPC) → toast persistente con Kill Switch habilitado
              toastStatusRef.current?.setStatus("error");
              const cleanMsg = errorMsg?.startsWith("409_CONFLICT::")
                ? "Error de validación en datos extraídos. Digita manualmente para rescatar."
                : (errorMsg || "Error al procesar documento");
              toastStatusRef.current?.setLabel(cleanMsg);
              const archivoUrl = payload.new?.archivo_url || "";
              if (archivoUrl) {
                toastStatusRef.current?.setKillSwitchContext(jobId, archivoUrl);
              }
              // Sin auto-dismiss: el usuario debe accionar (cerrar manual o usar Kill Switch).
              queryClient.invalidateQueries?.({ queryKey: ["failed-jobs"] });
            }
            cleanupChannel();
          }
        },
      )
      .subscribe((status) => {
        console.log("🔵 Estado suscripción Realtime:", status);
      });

    channelRef.current = channel;
  };

  const invokeExtraction = async (archivoUrl: string, jobId: string, replaceExisting = false) => {
    if (!user?.id || !orgId) throw new Error("Faltan credenciales de usuario/organización");

    const { data, error } = await supabase.functions.invoke("trigger-n8n-extraction", {
      body: {
        archivo_url: archivoUrl,
        user_id: user.id,
        org_id: orgId,
        replace_existing: replaceExisting,
        job_id: jobId,
      },
    });

    if (error) {
      const status = (error as any)?.context?.status || (error as any)?.status;
      if (status === 409) {
        let body: any = {};
        try { body = await (error as any).context.json(); } catch {
          try { body = JSON.parse(error.message); } catch { /* ignore */ }
        }
        const conflictError: any = new Error("Documento duplicado");
        conflictError.status = 409;
        conflictError.body = body;
        throw conflictError;
      }
      throw error;
    }
    return data;
  };

  const handleSend = async () => {
    if (!file || !user?.id || !orgId) return;
    const fileRef = file;

    setFile(null);
    onOpenChange(false);
    const { id: toastId } = showLoadingToast(fileRef.name);

    try {
      // Step 1: Upload to Storage
      toastStatusRef.current?.setLabel("Subiendo archivo...");
      const archivoUrl = await uploadToStorage(fileRef);

      // Step 2: Create job record
      toastStatusRef.current?.setLabel("Preparando procesamiento...");
      const jobId = await createJob(archivoUrl);

      // Step 3: Subscribe to Realtime updates BEFORE firing the function
      subscribeToJob(jobId, toastId);

      // Step 4: Fire-and-forget (non-blocking) — only handle immediate errors
      toastStatusRef.current?.setLabel("Enviando a extracción...");
      try {
        await invokeExtraction(archivoUrl, jobId, false);
        // 200/202 → control passes to WebSocket, nothing more to do here
      } catch (err: any) {
        if (err?.status === 409) {
          // Duplicate: cleanup channel, show duplicate modal
          cleanupChannel();
          dismiss(toastId);
          const errBody = err?.body || {};
          setPendingData({
            file: fileRef,
            archivoUrl,
            noDocumento: errBody.no_documento || fileRef.name,
            existingCount: errBody.existing_count || 1,
            existingIds: errBody.existing_ids || [],
            extractedPayload: errBody.extracted_payload || null,
          });
          setDuplicateModalOpen(true);
          return;
        }
        throw err;
      }
    } catch (err: any) {
      console.error("ProcessDocument error:", err);
      cleanupChannel();
      toastStatusRef.current?.setStatus("error");
      toastStatusRef.current?.setLabel("No se pudo enviar el documento");
      setTimeout(() => dismiss(toastId), 5000);
    }
  };

  const handleDuplicateCancel = () => {
    setPendingData(null);
    setDuplicateModalOpen(false);
  };

  const handleDuplicateReplace = async () => {
    if (!pendingData) return;
    const { archivoUrl, noDocumento, extractedPayload } = pendingData;

    setDuplicateModalOpen(false);
    const { id: toastId } = showLoadingToast(noDocumento);

    try {
      // Create a new job for the replacement
      const jobId = await createJob(archivoUrl);
      subscribeToJob(jobId, toastId);

      if (extractedPayload) {
        // FAST PATH: cached payload, skip re-extraction
        toastStatusRef.current?.setLabel("Guardando cambios...");
        const { error } = await supabase.functions.invoke("trigger-n8n-extraction", {
          body: {
            replace_existing: true,
            extracted_payload: extractedPayload,
            job_id: jobId,
          },
        });
        if (error) throw error;
      } else {
        // FALLBACK: re-extract with AI
        toastStatusRef.current?.setLabel("Reemplazando documento...");
        await invokeExtraction(archivoUrl, jobId, true);
      }
      // Control passes to WebSocket
    } catch (err: any) {
      console.error("Replace error:", err);
      cleanupChannel();
      toastStatusRef.current?.setStatus("error");
      toastStatusRef.current?.setLabel("No se pudo reemplazar el documento");
      setTimeout(() => dismiss(toastId), 5000);
    } finally {
      setPendingData(null);
    }
  };

  const handleClose = () => {
    setFile(null);
    onOpenChange(false);
  };

  const quotaBanner = quota.isAtLimit && !quota.isLoading ? (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 space-y-2">
      <div className="flex items-center gap-2 text-amber-400">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span className="text-sm font-semibold">Límite alcanzado</span>
      </div>
      <p className="text-xs text-muted-foreground">
        Has alcanzado el límite de tu plan <span className="font-semibold text-foreground">{quota.planLabel}</span> ({quota.used}/{quota.limit} documentos este mes). Contacta al administrador para solicitar un upgrade.
      </p>
      <Progress value={100} className="h-1.5 [&>div]:bg-amber-500" />
    </div>
  ) : null;

  const content = (
    <div className="w-full min-w-0 overflow-hidden py-4 px-1 space-y-4">
      {quotaBanner}
      {!quota.isAtLimit && (
        <>
          {!file ? (
            <button
              onClick={() => inputRef.current?.click()}
              className="w-full max-w-full border-2 border-dashed border-border rounded-xl p-8 flex flex-col items-center gap-3 hover:border-primary/50 hover:bg-muted/30 transition-all cursor-pointer"
            >
              <FileUp className="h-10 w-10 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Haz clic para seleccionar un archivo</span>
              <span className="text-xs text-muted-foreground">PDF, JPG, PNG</span>
            </button>
          ) : (
            <div className="grid w-full max-w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-lg border border-border bg-muted/30 p-4 overflow-hidden">
              <FileUp className="h-5 w-5 text-primary shrink-0" />
              <span className="min-w-0 max-w-full break-all text-sm leading-tight text-foreground" title={file.name}>
                {file.name}
              </span>
              <button
                onClick={() => setFile(null)}
                className="shrink-0 text-muted-foreground hover:text-destructive"
                aria-label="Quitar archivo"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            onChange={handleFileChange}
            className="hidden"
          />
        </>
      )}

      {/* Bandeja persistente: Procesamientos Fallidos rescatables vía Kill Switch */}
      {!quota.isAtLimit && (failedJobs.data?.length ?? 0) > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2">
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <FileWarning className="h-4 w-4 shrink-0" />
            <span className="text-xs font-semibold">Procesamientos fallidos ({failedJobs.data!.length})</span>
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug">
            La IA falló con estos documentos. Puedes rescatarlos digitándolos manualmente (consume 1 crédito por documento).
          </p>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {failedJobs.data!.map((job) => {
              const fileName = job.archivo_url.split("_").slice(1).join("_") || job.archivo_url;
              const ageRef = job.updated_at || job.created_at;
              return (
                <div
                  key={job.id}
                  className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 rounded-md border border-border bg-card p-2"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate" title={fileName}>{fileName}</p>
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <Clock className="h-2.5 w-2.5" />
                      {formatDistanceToNow(new Date(ageRef), { addSuffix: true, locale: es })}
                    </p>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-destructive/10 text-destructive border border-destructive/30">
                    error
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => launchKillSwitch(job.id, job.archivo_url)}
                    className="h-7 gap-1 text-xs border-amber-500/40 hover:bg-amber-500/10 text-amber-600 dark:text-amber-400"
                  >
                    <Wrench className="h-3 w-3" />
                    Digitar
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => requestDiscard(job.id, fileName)}
                    className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    title="Descartar (no consume crédito)"
                    aria-label="Descartar procesamiento fallido"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const actions = quota.isAtLimit ? (
    <Button variant="ghost" onClick={handleClose} className="text-muted-foreground">
      Cerrar
    </Button>
  ) : (
    <>
      <Button variant="ghost" onClick={handleClose} className="text-muted-foreground">
        Cancelar
      </Button>
      <Button
        onClick={handleSend}
        disabled={!file}
        className="bg-primary text-primary-foreground hover:bg-primary/90 glow-cyan-strong"
      >
        Enviar
      </Button>
    </>
  );

  if (isMobile) {
    return (
      <>
        <Drawer open={open} onOpenChange={handleClose}>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-primary" />
                Procesar Documento
              </DrawerTitle>
            </DrawerHeader>
            <div className="px-4">{content}</div>
            <DrawerFooter className="flex-row justify-end gap-2">{actions}</DrawerFooter>
          </DrawerContent>
        </Drawer>
        <DuplicateDocumentModal
          open={duplicateModalOpen}
          noDocumento={pendingData?.noDocumento || ""}
          existingCount={pendingData?.existingCount || 0}
          existingIds={pendingData?.existingIds}
          onCancel={handleDuplicateCancel}
          onReplace={handleDuplicateReplace}
          replacing={false}
        />
        <AssignEmbarqueModal open={assignModalOpen} onOpenChange={setAssignModalOpen} documentId={assignDocId} />
        <ManualIngestModal open={killSwitchOpen} onOpenChange={setKillSwitchOpen} jobId={killSwitchJobId} archivoUrl={killSwitchArchivoUrl} />
        <DiscardDialog />
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="glass border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              Procesar Documento
            </DialogTitle>
          </DialogHeader>
          {content}
          <DialogFooter>{actions}</DialogFooter>
        </DialogContent>
      </Dialog>
      <DuplicateDocumentModal
        open={duplicateModalOpen}
        noDocumento={pendingData?.noDocumento || ""}
        existingCount={pendingData?.existingCount || 0}
        existingIds={pendingData?.existingIds}
        onCancel={handleDuplicateCancel}
        onReplace={handleDuplicateReplace}
        replacing={false}
      />
      <AssignEmbarqueModal open={assignModalOpen} onOpenChange={setAssignModalOpen} documentId={assignDocId} />
      <ManualIngestModal open={killSwitchOpen} onOpenChange={setKillSwitchOpen} jobId={killSwitchJobId} archivoUrl={killSwitchArchivoUrl} />
      <DiscardDialog />
    </>
  );

  function DiscardDialog() {
    return (
      <AlertDialog open={!!discardJobId} onOpenChange={(v) => !v && !discarding && setDiscardJobId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar procesamiento fallido</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a descartar <span className="font-medium text-foreground">{discardFileName}</span>. No se digitará el documento ni se consumirá crédito. Esta acción se registrará en auditoría y el archivo dejará de aparecer en la bandeja de fallidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={discarding}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDiscard(); }}
              disabled={discarding}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {discarding && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }
}
